import { create } from 'zustand';
import { websocketService } from '@/lib/websocketService'; // Import websocketService
import type { NewNotificationData } from '@/lib/websocketService'; // Import type for WS payload
import useProjectStore from './useProjectStore'; // Import project store for refetching
import { NotificationToastContent } from '@/components/ui/NotificationToastContent';

// Re-using types from NotificationsPage.tsx for consistency
// In a larger app, these might live in a shared types/interfaces directory
interface ActorInfo {
  id: string | null;
  username: string | null;
  fullName: string | null;
  avatarKey: string | null;
}


interface ReferenceInfo {
  id: string | null;
  type: string | null;
}

export interface Notification {
  id: string;
  type: string;
  actor: ActorInfo | null;
  reference?: ReferenceInfo;
  summary: string;
  link?: string | null;
  isRead: boolean;
  createdAt: number;
}

interface NotificationsApiResponse {
  data: Notification[];
  meta: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasFetchedInitial: boolean;
  requestNotificationPermission: () => Promise<NotificationPermission>;
  registerBackgroundSync: () => Promise<void>;
  registerPeriodicSync: () => Promise<void>;

  fetchNotifications: (limit?: number, offset?: number) => Promise<void>;
  addNotification: (notification: Notification) => void; // For WebSocket updates
  setNotificationReadStatus: (notificationId: string, isRead: boolean) => Promise<void>;
  markAllNotificationsAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  _recalculateUnreadCount: () => void;
}

const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  hasFetchedInitial: false,

  requestNotificationPermission: async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      return Notification.requestPermission();
    }
    return Notification.permission;
  },

  _recalculateUnreadCount: () => {
    const unread = get().notifications.filter(n => !n.isRead).length;
    set({ unreadCount: unread });

    // Update app badge for PWA
    if ('setAppBadge' in navigator) {
      if (unread > 0) {
        navigator.setAppBadge(unread);
      } else {
        navigator.clearAppBadge();
      }
    }
  },

  registerBackgroundSync: async () => {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const registration = await navigator.serviceWorker.ready;
      try {
        // @ts-expect-error - experimental API
        await registration.sync.register('database-sync');
        console.log('Background sync registered for notifications');
      } catch (error) {
        console.log('Background Sync failed', error);
      }
    }
  },

  registerPeriodicSync: async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      try {
        const permission = await navigator.permissions.query({ name: 'periodic-background-sync' as PermissionName });
        if (permission.state === 'granted') {
          // @ts-expect-error - experimental API
          await registration.periodicSync.register('fetch-new-content', {
            minInterval: 24 * 60 * 60 * 1000, // Once a day
          });
          console.log('Periodic sync registered for notifications');
        }
      } catch (error) {
        console.log('Periodic Sync failed', error);
      }
    }
  },

  fetchNotifications: async (limit = 20, offset = 0) => {
    if (get().isLoading && get().hasFetchedInitial) return; // Prevent multiple initial fetches if already loading
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`/api/me/notifications?limit=${limit}&offset=${offset}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch notifications' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      const result: NotificationsApiResponse = await response.json();
      // If it's an initial fetch or a "load more" scenario, decide how to merge/set notifications
      // For now, simple set, assuming initial load.
      set({ notifications: result.data, isLoading: false, hasFetchedInitial: true });
      get()._recalculateUnreadCount();
    } catch (error: unknown) {
      set({ error: (error as Error).message, isLoading: false });
      console.error("Fetch notifications store error:", error);
    }
  },

  addNotification: (notification: Notification) => {
    set(state => ({
      notifications: [notification, ...state.notifications], // Add to the beginning
    }));
    get()._recalculateUnreadCount();
    // TODO: Potentially limit the number of notifications stored in memory
  },

  setNotificationReadStatus: async (notificationId: string, isRead: boolean) => {
    // if (!logged in) {
    //     console.error("Auth not available for updating notification status.");
    //     set({ error: "Authentication required to update notification."});
    //     return;
    // }
    // Optimistic update
    const originalNotifications = get().notifications;
    set(state => ({
      notifications: state.notifications.map(n =>
        n.id === notificationId ? { ...n, isRead } : n
      ),
    }));
    get()._recalculateUnreadCount();

    try {
      const response = await fetch(`/api/me/notifications/${notificationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ isRead }),
      });
      if (!response.ok) {
        // Revert optimistic update on failure
        set({ notifications: originalNotifications, error: "Failed to update notification on server." });
        get()._recalculateUnreadCount();
        const errorData = await response.json().catch(() => ({}));
        console.error("Set notification read status error:", errorData.message || response.statusText);
      }
    } catch (error: unknown) {
      // Revert optimistic update on failure
      set({ notifications: originalNotifications, error: (error as Error).message });
      get()._recalculateUnreadCount();
      console.error("Set notification read status error:", error);
    }
  },

  markAllNotificationsAsRead: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    const originalNotifications = get().notifications;

    // Optimistic update
    set(state => ({
      notifications: state.notifications.map(n => ({ ...n, isRead: true })),
    }));
    get()._recalculateUnreadCount();

    try {
      const response = await fetch(`/api/me/notifications/mark-all-read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for cookies
      });
      if (!response.ok) {
        set({ notifications: originalNotifications, error: "Failed to mark all notifications as read on server." }); // Revert
        get()._recalculateUnreadCount();
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      // Data is already optimistically updated.
      set({ isLoading: false });
    } catch (error: unknown) {
      set({ notifications: originalNotifications, error: (error as Error).message, isLoading: false }); // Revert
      get()._recalculateUnreadCount();
      console.error("Mark all notifications as read error:", error);
    }
  },
  deleteNotification: async (notificationId: string) => {
    if (get().isLoading) return;
    const originalNotifications = get().notifications;
    const notificationToDelete = originalNotifications.find(n => n.id === notificationId);
    if (!notificationToDelete) {
      set({ error: "Notification not found." });
      return;
    }

    // Optimistic update: remove from list
    set(state => ({
      notifications: state.notifications.filter(n => n.id !== notificationId),
    }));
    get()._recalculateUnreadCount();

    try {
      const response = await fetch(`/api/me/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        // Revert optimistic update on failure
        set({ notifications: originalNotifications, error: "Failed to delete notification on server." });
        get()._recalculateUnreadCount();
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      // Success, stay removed
      set({ isLoading: false });
    } catch (error: unknown) {
      // Revert optimistic update on failure
      set({ notifications: originalNotifications, error: (error as Error).message, isLoading: false });
      get()._recalculateUnreadCount();
      console.error("Delete notification error:", error);
    }
  },
}));

// Initialize WebSocket listener for notifications
if (typeof window !== 'undefined') { // Ensure this runs only on client-side
  // WebSocket connection will be established by components that need it
  // The listener is set up here to receive notifications when the connection is active

  websocketService.onNewNotification((wsNotification: NewNotificationData) => {
    // Transform NewNotificationData to the store's Notification type
    const newNotification: Notification = {
      id: wsNotification.notification_id,
      type: wsNotification.notification_type,
      actor: {
        id: wsNotification.actor_user_id || null,
        username: wsNotification.actor_username || null,
        fullName: null, // NewNotificationData doesn't have actor_full_name, might need to add if desired
        avatarKey: wsNotification.actor_avatar_key || null,
      },
      reference: wsNotification.reference_item_id && wsNotification.reference_item_type ? {
        id: wsNotification.reference_item_id,
        type: wsNotification.reference_item_type,
      } : undefined,
      summary: wsNotification.message_summary,
      link: wsNotification.link_target_url || null,
      isRead: wsNotification.is_read, // Should typically be false from WS
      createdAt: wsNotification.created_at * 1000, // Convert from seconds to milliseconds for JavaScript Date
    };

    // Avoid adding duplicate notifications if already fetched via HTTP
    const existing = useNotificationStore.getState().notifications.find(n => n.id === newNotification.id);
    if (!existing) {
      useNotificationStore.getState().addNotification(newNotification);

      // Show in-app toast for new notification
      const richMessage = <NotificationToastContent wsNotification={wsNotification} />;
      window.dispatchEvent(new CustomEvent('toast:push', { detail: { message: richMessage, type: 'info', duration: 10000 } }));

      // Show browser notification if tab is not active and permission granted
      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Syncreos', {
          body: wsNotification.message_summary,
          icon: '/logos/icon512_maskable.png',
          badge: '/logos/android/android-launchericon-96-96.png',
          tag: wsNotification.notification_id, // Prevent duplicates
          data: {
            path: wsNotification.link_target_url || '/notifications',
            notificationId: wsNotification.notification_id
          }
        }).onclick = () => {
          window.focus();
          // Could navigate to the link
        };
      }
    } else {
      // Optionally update if WS has newer info, though unlikely for basic add.
      // For now, if it exists, we assume it's up-to-date from HTTP or a previous WS message.
      console.log("WS Notification: Already exists, id:", newNotification.id);
    }

    // Handle project join approval - refetch project data to update contributor status
    if (wsNotification.notification_type === 'project_join_approved' && wsNotification.reference_item_id) {
      console.log('🔄 Project join approved notification received, refetching project:', wsNotification.reference_item_id);
      // Refetch the project to update isContributor status and contributors list
      useProjectStore.getState().fetchProjectById(wsNotification.reference_item_id).then(project => {
        console.log('✅ Project refetched after join approval:', project?.title, 'isContributor:', project?.isContributor);
      }).catch(error => {
        console.error('❌ Failed to refetch project after join approval:', error);
      });
    }

    // Handle project joined (via invite acceptance) - refetch project data to update contributor status
    if (wsNotification.notification_type === 'project_joined' && wsNotification.reference_item_id) {
      console.log('🔄 Project joined notification received, refetching project:', wsNotification.reference_item_id);
      // Refetch the project to update isContributor status and contributors list
      useProjectStore.getState().fetchProjectById(wsNotification.reference_item_id).then(project => {
        console.log('✅ Project refetched after joining:', project?.title, 'isContributor:', project?.isContributor);
      }).catch(error => {
        console.error('❌ Failed to refetch project after joining:', error);
      });
    }
  });
}

export default useNotificationStore;
